import {
  Component,
  Element,
  h,
  Host,
  JSX,
  Listen,
  Prop,
  State,
} from "@stencil/core";
import {
  hideMoreButton,
  isMoreButtonHidden,
} from "@phn/services/more-indicator-service";
import { componentsOnReady } from "@phn/utility/ready";
import { Timeout } from "@phn/utility/timeout";
import { motionDurationShort } from "@porsche-design-system/components-js/styles";
import { navContentStore } from "@phn/state/nav-content-store";
import {
  ClickElementTypes,
  ElementTypes,
  EventActions,
} from "@phn/utility/constants";
import { GlobalDataLayerService } from "@phn/services/analytics/global-data-layer-service";
import analyticsManager from "@phn/services/analytics/analytics-manager";
import { getCurrentRoute } from "@phn/services/routing-manager";

type Level = "l2" | "l3";

@Component({
  tag: "phn-nd-more-content-indicator",
  styleUrl: "more-content-indicator.scss",
  scoped: true,
})
export class MoreContentIndicator {
  private readonly buttonId = "scrollBtn";

  @Prop() level: Level = "l2";

  @State() showButton = false;

  @State() eventScrollListener = this.endScroll.bind(this);

  @Element() el!: HTMLPhnNdMoreContentIndicatorElement;

  @State() scrollContainer =
    this.el.closest("#main-drawer") || this.el.closest(".drawer-content"); // Desktop or Mobile

  @Listen("bodyTypeChange", { target: "window", passive: true })
  async bodyTypeChangeHandler(): Promise<void> {
    this.showButton = false;
    await componentsOnReady(this.el, new Timeout(5000));
    this.initScrollButton();
  }

  async componentDidLoad() {
    if (!isMoreButtonHidden()) {
      this.scrollContainer?.addEventListener(
        "scroll",
        this.eventScrollListener,
        { once: true },
      );
    }

    await componentsOnReady(this.el, new Timeout(5000));
    this.initScrollButton();
  }

  disconnectedCallback() {
    this.scrollContainer?.removeEventListener(
      "scroll",
      this.eventScrollListener,
    );
  }

  private endScroll() {
    if (this.showButton) {
      this.el.firstElementChild.classList.add("bottom-out");

      setTimeout(
        () => {
          this.showButton = false;
          hideMoreButton();
        },
        Number(motionDurationShort.replace("s", "")) * 1000,
      );
    }
  }

  /**
   * Check if parent element is bigger then viewport and only show button then
   */
  private initScrollButton() {
    if (!isMoreButtonHidden()) {
      const listHeight = this.scrollContainer?.scrollHeight;
      const viewportHeight = window.innerHeight;

      if (listHeight > viewportHeight && this.scrollContainer.scrollTop === 0) {
        this.showButton = true;
      }
    } else {
      this.showButton = false;
    }
  }

  private scrollToBottom() {
    GlobalDataLayerService.getInstance().pushDataLayerEvent(
      EventActions.SHOW_MORE_CLICK,
      {
        componentClick: {
          clickElementId: this.buttonId,
          clickElementName: navContentStore.state.scrollingIndicator,
          targetElement: "anchor",
          clickElementType: ClickElementTypes.Button,
        },
        componentDisplay: {
          displayElementId: analyticsManager.calculateDisplayElementId(),
          displayElementType: analyticsManager.calculateDisplayElementType(
            getCurrentRoute(),
            ElementTypes.showMoreButton,
          ),
          displayElementName: navContentStore.state.scrollingIndicator,
        },
      },
    );
    this.scrollContainer?.scroll?.({
      top: this.scrollContainer.scrollHeight,
      behavior: "smooth",
    });
  }

  render(): JSX.Element {
    const text = navContentStore.state.scrollingIndicator;

    return (
      <Host>
        {this.showButton ? (
          <div class="more-indicator-container-animation more-indicator-container fade-in">
            <button
              class={`more-indicator-button btn-${this.level}`}
              aria-hidden={true}
              id={this.buttonId}
              onClick={() => this.scrollToBottom()}
            >
              <phn-p-icon
                class={"bounce"}
                color="primary"
                name="arrow-down"
              ></phn-p-icon>
              <span>{text}</span>
            </button>
          </div>
        ) : null}
      </Host>
    );
  }
}
